// ─────────────────────────────────────────────────────────────
// AliasService — product_aliases 테이블 책임 격리
// 모든 함수는 순수 DB 작업만 담당 (React state 변경 X)
// 호출자(App.js)가 결과를 받아서 setState 등 처리
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

export const AliasService = {
  // 별칭 → 표준 제품명 조회 (대소문자 무시)
  // returns: {alias, product_name} | null
  async lookup(name) {
    if (!name) return null;
    const { data } = await supabase.from('product_aliases')
      .select('alias,product_name')
      .ilike('alias', name)
      .limit(1);
    return (data && data.length > 0) ? data[0] : null;
  },

  // v7.279: 별칭 배열 → { 별칭소문자: 표준제품명 } 일괄 조회
  //   모바일 스캔의 다중 바코드 분류에서 쓴다. 한 프레임에 코드가 여러 개라
  //   lookup() 을 코드마다 부르면 왕복이 늘어난다.
  //   ⚠ 대소문자 무시 매칭이 필요한데 .in() 은 대소문자를 구분한다.
  //     → 원본/대문자/소문자 후보를 함께 넣고, 결과는 소문자 키로 정규화해 돌려준다.
  async lookupMany(names) {
    if (!names || names.length === 0) return {};
    const cand = [...new Set(names.flatMap(n => [n, n.toUpperCase(), n.toLowerCase()]))];
    const { data, error } = await supabase.from('product_aliases')
      .select('alias,product_name')
      .in('alias', cand);
    if (error) return {};
    const map = {};
    (data || []).forEach(r => { map[(r.alias || '').toLowerCase()] = r.product_name; });
    return map;
  },

  // 특정 제품의 별칭 목록
  async listFor(productName) {
    const { data, error } = await supabase.from('product_aliases')
      .select('id,alias,source,created_at,note')
      .eq('product_name', productName)
      .order('alias');
    if (error) return [];
    return data || [];
  },

  // 별칭 부분 검색 (%query%)
  async searchByQuery(query, limit = 8) {
    if (!query) return [];
    const { data } = await supabase.from('product_aliases')
      .select('alias,product_name')
      .ilike('alias', `%${query}%`)
      .limit(limit);
    return data || [];
  },

  // 제품명 배열 → {product_name: count} 매핑 (별칭 개수)
  async countsForProducts(names) {
    if (!names || names.length === 0) return {};
    const { data } = await supabase.from('product_aliases')
      .select('product_name')
      .in('product_name', names);
    const counts = {};
    (data || []).forEach(a => { counts[a.product_name] = (counts[a.product_name] || 0) + 1; });
    return counts;
  },

  // 별칭이 이미 등록되어 있는지 확인 (대소문자 무시)
  // returns: {product_name} | null
  async findOwner(alias) {
    if (!alias) return null;
    const { data } = await supabase.from('product_aliases')
      .select('product_name')
      .ilike('alias', alias)
      .limit(1);
    return (data && data.length > 0) ? data[0] : null;
  },

  // 별칭 추가 — 검증까지 포함
  // returns: { ok: true } | { ok: false, msg }
  async create(productName, alias, source = 'manual') {
    const a = (alias || '').trim();
    if (!a) return { ok: false, msg: '별칭을 입력하세요.' };
    if (a.toLowerCase() === productName.toLowerCase()) {
      return { ok: false, msg: '별칭이 제품명과 동일합니다.' };
    }
    // 이미 존재하는 alias인지 확인
    const existing = await AliasService.findOwner(a);
    if (existing) {
      return { ok: false, msg: `이미 "${existing.product_name}"의 별칭으로 등록되어 있습니다.` };
    }
    const { error } = await supabase.from('product_aliases')
      .insert({ alias: a, product_name: productName, source });
    if (error) return { ok: false, msg: '별칭 등록 실패: ' + error.message };
    return { ok: true };
  },

  // 별칭 삭제
  // returns: { ok: true } | { ok: false, msg }
  async remove(id) {
    const { error } = await supabase.from('product_aliases').delete().eq('id', id);
    if (error) return { ok: false, msg: '삭제 실패: ' + error.message };
    return { ok: true };
  },
};
